'use client';

import React, { useState, useEffect } from 'react';

import styled from 'styled-components';

import ReponsiveImage from '@/ui/components/Image/Responsive';
import { device } from '@/ui/styles/breakpoints';
import { getYoutubeId } from '@/utils/helpers';

type MediaWrapProps = {
  $isPageLoading?: boolean;
  $isLoading?: boolean;
  $isTabOpen: boolean;
};

const MediaWrap = styled.div<MediaWrapProps>`
  aspect-ratio: 4 / 3;
  display: block;
  border: 1px
    ${props =>
      props.$isPageLoading ? 'transparent' : props.theme.palette.gray.main}
    solid;
  height: ${props => (props.$isTabOpen ? 'auto' : '100%')};
  overflow: hidden;
  position: relative;

  @media ${device.max.md} {
    aspect-ratio: auto;
  }

  ${props =>
    !props.$isTabOpen &&
    `
    @media (max-height: 950px) and (min-width: 901px) {
      display: flex;
      height: 100%;
      flex-grow: 1;
      min-height: 289px;
      width: 100%;
    }
  `}

  img, iframe, .pulse {
    aspect-ratio: ${props => (props.$isLoading ? '768/576' : 'auto')};
    height: auto;
    opacity: ${props => (props.$isLoading ? '0' : '1')};
    width: 100%;

    ${props =>
      !props.$isTabOpen &&
      `
    @media (max-height: 950px) and (min-width: 901px) {
      height: 100%;
      left: 0;
      position: absolute;
      object-position: left top;
      object-fit: contain;
      top: 0;
      width: 100%;
    }
  `}

    @media ${device.max.md} {
      border-left-width: 0;
      border-right-width: 0;
      height: 190px;
      margin-left: auto;
      margin-right: auto;
      width: auto;
    }
  }

  iframe {
    height: 100%;

    @media ${device.max.md} {
      width: 100%;
    }
  }

  .loadingForImage {
    align-items: center;
    display: flex;
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;

    div {
      margin-left: auto;
      margin-right: auto;
    }
  }
`;

// Update the ImageData interface to match actual data structure
interface Resolution {
  width: number;
  height: number;
}

interface ImageResolutions {
  original: Resolution;
  medium: Resolution;
  large: Resolution;
  thumbnail: Resolution;
  medium_large: Resolution;
  '1536x1536': Resolution;
}

interface ImageData {
  id: number;
  resolution: ImageResolutions;
  original: string;
  medium: string;
  large: string;
  thumbnail: string;
  medium_large: string;
  '1536x1536': string;
  srcset: string;
  child?: ImageData;
}

// Update MediaDetail component types
interface MediaDetailProps {
  imageData?: ImageData;
  src?: string;
  alt?: string;
  youtubeSrc?: string;
  isTabOpen: boolean;
  isPageLoading?: boolean;
}

export function MediaDetail({
  imageData,
  src,
  alt,
  youtubeSrc,
  isTabOpen,
  isPageLoading = false,
}: MediaDetailProps) {
  const [isLoading, setIsLoading] = useState(true);

  // Set loading state based on content type
  useEffect(() => {
    // If we have YouTube content, never show loading
    if (youtubeSrc) {
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
  }, [src, youtubeSrc]);

  const handleImageLoaded = () => {
    setIsLoading(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
  };

  const checkIfImageIsCached = imgSrc => {
    const image = new Image();
    image.src = imgSrc;
    return image.complete;
  };

  useEffect(() => {
    if (src && checkIfImageIsCached(src)) {
      handleImageLoaded();
    }
  }, [src]);

  return (
    <MediaWrap
      $isPageLoading={isPageLoading}
      $isLoading={isLoading}
      $isTabOpen={isTabOpen}
    >
      {isPageLoading ? (
        <div className="pulse" style={{ opacity: 1 }} />
      ) : (
        <>
          {!youtubeSrc && isLoading && (
            <div className="loadingForImage">
              <div>Loading...</div>
            </div>
          )}
          {imageData ? (
            <ReponsiveImage
              zoom={true}
              alt={alt}
              imageData={imageData}
              onLoad={handleImageLoaded}
              onError={handleImageError}
            />
          ) : (
            youtubeSrc && (
              <iframe
                width="560"
                height="315"
                src={`https://www.youtube.com/embed/${getYoutubeId(
                  youtubeSrc
                )}`}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            )
          )}
        </>
      )}
    </MediaWrap>
  );
}
